import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../../../../types/types';
import { AppSyncUser } from '../../../../services/auth/types/user';
import { getTrips, getFilteredTrips, Trip } from '../../../../services/trips';
import { TripCard } from './helpers';
import { EmptyState } from '../TripDetail/helpers';
import './Trips.css';

interface TripsProps {
  user: UserProfile;
  appSyncUser: AppSyncUser | null;
}

const Trips: React.FC<TripsProps> = ({ user, appSyncUser }) => {
  const navigate = useNavigate();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const fetchedTrips = await getTrips();
        console.log('📋 Trips fetched from API:', fetchedTrips);
        console.log('📋 Trip IDs:', fetchedTrips.map(t => ({ id: t.id, title: t.title })));
        setTrips(fetchedTrips);
      } catch (err: any) {
        console.error('Failed to fetch trips:', err);
        setError(err.message || 'Failed to load trips. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrips();
  }, []);

  const handleTripClick = (tripId: string | number) => {
    console.log('🔍 handleTripClick: Navigating to trip with ID:', tripId);
    console.log('🔍 Full URL will be:', `/trips/${tripId}`);
    navigate(`/trips/${tripId}`);
  };

  const handleCreateDraftTrip = () => {
    navigate('/trips/new');
  };

  // Filter trips by status using shared utility
  const { drafted, inProgress, upcoming, past } = getFilteredTrips(trips);

  if (isLoading) {
    return (
      <div className="trips-content">
        <div className="trips-header">
          <h2>My Trips</h2>
          <p>Your travel adventures and upcoming journeys</p>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your trips...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trips-content">
        <div className="trips-header">
          <h2>My Trips</h2>
          <p>Your travel adventures and upcoming journeys</p>
        </div>
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error loading trips</h3>
          <p>{error}</p>
          <button 
            className="btn-primary" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="trips-content">
      <div className="trips-header">
        <h2>My Trips</h2>
        <p>Your travel adventures and upcoming journeys</p>
      </div>

      {/* Drafts Section */}
      <div className="trips-section">
        <h3>Drafts</h3>
        <div className="trips-grid">
          {/* Create New Trip Card - Always First */}
          <div className="trip-card create-trip-card" onClick={handleCreateDraftTrip}>
            <div className="trip-image create-trip-image">
              <span className="trip-icon">➕</span>
            </div>
            <div className="trip-details">
              <h4>Create New Trip</h4>
              <p className="create-trip-description">Start planning your next adventure</p>
              <div className="trip-actions">
                <button 
                  className="btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateDraftTrip();
                  }}
                >
                  Create Trip
                </button>
              </div>
            </div>
          </div>
          
          {/* Existing Draft Trips */}
          {drafted.map((trip: Trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              onViewDetails={handleTripClick}
            />
          ))}
        </div>
      </div>

      {/* In Progress Trips */}
      <div className="trips-section">
        <h3>In Progress</h3>
        {inProgress.length > 0 ? (
          <div className="trips-grid">
            {inProgress.map((trip: Trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                onViewDetails={handleTripClick}
              />
            ))}
          </div>
        ) : (
          <EmptyState 
            icon="🚀" 
            message="No trips in progress"
            className="trips-empty-state"
          />
        )}
      </div>

      {/* Upcoming Trips */}
      <div className="trips-section">
        <h3>Upcoming Trips</h3>
        {upcoming.length > 0 ? (
          <div className="trips-grid">
            {upcoming.map((trip: Trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                onViewDetails={handleTripClick}
              />
            ))}
          </div>
        ) : (
          <EmptyState 
            icon="📅" 
            message="No upcoming trips"
            className="trips-empty-state"
          />
        )}
      </div>

      {/* Past Trips */}
      <div className="trips-section">
        <h3>Past Adventures</h3>
        {past.length > 0 ? (
          <div className="trips-grid">
            {past.map((trip: Trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                onViewDetails={handleTripClick}
                variant="past"
              />
            ))}
          </div>
        ) : (
          <EmptyState 
            icon="📸" 
            message="No past trips yet"
            className="trips-empty-state"
          />
        )}
      </div>

      {/* Empty State for New Users */}
      {upcoming.length === 0 && inProgress.length === 0 && past.length === 0 && drafted.length === 0 && (
        <EmptyState
          icon="✈️"
          title="No trips yet!"
          message="Start planning your first adventure and create amazing memories."
          actionLabel="Plan Your First Trip"
          onAction={handleCreateDraftTrip}
          className="empty-state"
        />
      )}
    </div>
  );
};

export default Trips;
